import { styled } from "@mui/material";

/**
 * Styled primitives for the Custom Gradient builder. Aesthetic deliberately
 * matches the surrounding wallpaper picker (DM Sans, purple accents #81459B /
 * #99439D, soft #f6f7f9 chips, 8px radii) — no new design language introduced.
 */

export const Root = styled("div")`
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-top: 14px;
  padding-top: 12px;
  border-top: 1px solid #f0e8f4;
  font-family: "DM Sans", sans-serif;
`;

export const SectionLabel = styled("p")`
  font-size: 13px;
  font-weight: 600;
  color: #4a4a4a;
  margin: 0 0 6px 4px;
  font-family: "DM Sans", sans-serif;
`;

export const Row = styled("div")`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
`;

/** Segmented pill group (used for type + linear-direction presets). */
export const SegmentGroup = styled("div")`
  display: inline-flex;
  background: #f6f7f9;
  border-radius: 10px;
  padding: 3px;
  gap: 3px;
`;

export const SegmentButton = styled("button")`
  appearance: none;
  border: none;
  cursor: pointer;
  font-family: "DM Sans", sans-serif;
  font-size: 12px;
  font-weight: 600;
  padding: 6px 12px;
  border-radius: 8px;
  text-transform: capitalize;
  color: ${({ active }) => (active ? "#ffffff" : "#6b6b6b")};
  background: ${({ active }) => (active ? "#81459B" : "transparent")};
  transition: background 0.15s ease, color 0.15s ease;

  &:hover {
    background: ${({ active }) => (active ? "#81459B" : "#ececf0")};
  }

  &:focus-visible {
    outline: 2px solid #c283da;
    outline-offset: 1px;
  }
`;

export const Preview = styled("div")`
  width: 100%;
  height: 64px;
  border-radius: 10px;
  border: 1px solid #e1e4e8;
  background: ${({ css }) => css || "transparent"};
`;

export const AngleRow = styled("div")`
  display: flex;
  align-items: center;
  gap: 10px;
`;

export const AngleInput = styled("input")`
  width: 58px;
  font-family: "DM Sans", sans-serif;
  font-size: 12px;
  padding: 6px 8px;
  border: 1px solid #e1e4e8;
  border-radius: 8px;
  text-align: center;

  &:focus-visible {
    outline: 2px solid #c283da;
    outline-offset: 0;
    border-color: #c283da;
  }
`;

export const StopList = styled("div")`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

export const StopRow = styled("div")`
  display: flex;
  align-items: center;
  gap: 8px;
`;

export const StopSwatch = styled("button")`
  appearance: none;
  cursor: pointer;
  width: 28px;
  height: 28px;
  flex: 0 0 auto;
  border-radius: 50%;
  border: 1px solid #d5d9e0;
  background: ${({ color }) => color || "#ffffff"};
  box-shadow: rgba(0, 0, 0, 0.16) 0px 1px 4px;

  &:focus-visible {
    outline: 2px solid #c283da;
    outline-offset: 1px;
  }
`;

export const StopPosInput = styled("input")`
  width: 52px;
  font-family: "DM Sans", sans-serif;
  font-size: 12px;
  padding: 5px 6px;
  border: 1px solid #e1e4e8;
  border-radius: 8px;
  text-align: center;

  &:focus-visible {
    outline: 2px solid #c283da;
    border-color: #c283da;
  }
`;

export const IconButton = styled("button")`
  appearance: none;
  border: none;
  cursor: pointer;
  width: 26px;
  height: 26px;
  flex: 0 0 auto;
  border-radius: 7px;
  background: #f6f7f9;
  color: #6b6b6b;
  font-size: 16px;
  line-height: 1;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  transition: background 0.15s ease, color 0.15s ease;

  &:hover:not(:disabled) {
    background: #ececf0;
    color: #81459b;
  }

  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }

  &:focus-visible {
    outline: 2px solid #c283da;
    outline-offset: 1px;
  }
`;

export const AddStopButton = styled("button")`
  appearance: none;
  cursor: pointer;
  align-self: flex-start;
  font-family: "DM Sans", sans-serif;
  font-size: 12px;
  font-weight: 600;
  color: #81459b;
  background: transparent;
  border: 1px dashed #c283da;
  border-radius: 8px;
  padding: 6px 12px;
  transition: background 0.15s ease;

  &:hover:not(:disabled) {
    background: #faf4fd;
  }

  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }

  &:focus-visible {
    outline: 2px solid #c283da;
    outline-offset: 1px;
  }
`;

export const ApplyButton = styled("button")`
  appearance: none;
  cursor: pointer;
  font-family: "DM Sans", sans-serif;
  font-size: 13px;
  font-weight: 700;
  color: #ffffff;
  background: #81459b;
  border: none;
  border-radius: 9px;
  padding: 9px 16px;
  margin-top: 4px;
  transition: background 0.15s ease;

  &:hover {
    background: #6f3a86;
  }

  &:focus-visible {
    outline: 2px solid #c283da;
    outline-offset: 2px;
  }
`;

export const Hint = styled("p")`
  font-size: 11px;
  color: #9a9aa0;
  margin: 0 0 0 4px;
  font-family: "DM Sans", sans-serif;
`;

/* -------------------------------------------------------------------------- */
/* Draggable stop-track — the "pro" / intuitive layer that sits above the      */
/* numeric stop list. Same purple/DM-Sans aesthetic; no new design language.   */
/* -------------------------------------------------------------------------- */

/** Outer wrapper that reserves space for the handles hanging below the bar. */
export const TrackWrap = styled("div")`
  position: relative;
  width: 100%;
  padding: 6px 10px 26px;
  font-family: "DM Sans", sans-serif;
`;

/**
 * The horizontal gradient bar the handles sit on. The `css` prop carries the
 * live gradient string so the bar always previews the current gradient. The
 * bar is the click target for ADDING a stop (handled in JS via the click %).
 */
export const TrackBar = styled("div")`
  position: relative;
  width: 100%;
  height: 26px;
  border-radius: 8px;
  border: 1px solid #e1e4e8;
  background: ${({ css }) => css || "transparent"};
  cursor: ${({ disabled }) => (disabled ? "default" : "copy")};
  /* subtle checkerboard peeks through transparent stops */
  box-shadow: inset 0 0 0 1px rgba(255, 255, 255, 0.4);
  touch-action: none;
`;

/**
 * A draggable color handle pinned at its stop's position%. `left` is the
 * position percent; transform centers it on that point. Selected handles ride
 * above the rest and gain a purple ring.
 */
export const TrackHandle = styled("button")`
  position: absolute;
  top: 50%;
  left: ${({ position }) => `${position}%`};
  transform: translate(-50%, -50%);
  width: 20px;
  height: 20px;
  padding: 0;
  border-radius: 50%;
  border: 2px solid #ffffff;
  background: ${({ color }) => color || "#ffffff"};
  box-shadow: ${({ selected }) =>
    selected
      ? "0 0 0 2px #81459b, 0 1px 5px rgba(0,0,0,0.35)"
      : "0 1px 4px rgba(0,0,0,0.3)"};
  cursor: grab;
  z-index: ${({ selected }) => (selected ? 4 : 3)};
  transition: box-shadow 0.12s ease;
  touch-action: none;

  &:active {
    cursor: grabbing;
  }

  &:focus-visible {
    outline: 2px solid #c283da;
    outline-offset: 2px;
  }
`;

/** Small ✕ affordance on the selected handle to remove that stop. */
export const HandleRemove = styled("button")`
  position: absolute;
  top: -9px;
  right: -9px;
  width: 16px;
  height: 16px;
  padding: 0;
  border: none;
  border-radius: 50%;
  background: #ffffff;
  color: #81459b;
  font-size: 11px;
  line-height: 1;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.3);
  cursor: pointer;
  z-index: 5;

  &:hover {
    background: #81459b;
    color: #ffffff;
  }

  &:disabled {
    opacity: 0.35;
    cursor: not-allowed;
  }

  &:focus-visible {
    outline: 2px solid #c283da;
    outline-offset: 1px;
  }
`;

/** Helper caption under the track. */
export const TrackHint = styled("p")`
  font-size: 11px;
  color: #9a9aa0;
  margin: 4px 0 0 4px;
  font-family: "DM Sans", sans-serif;
`;
